import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { REQUEST_TEAM_API } from '../constants/routes';

interface RequestTeamErrorState {
    teamName?: string;
    email?: string;
    General?: string;
}

export default function RequestTeam() {
    const [teamName, setTeamName] = useState('');
    const [email, setEmail] = useState('');
    const [error, setError] = useState<RequestTeamErrorState>({});
    const [isPending, setIsPending] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError({});

        if (!teamName.trim()) {
            setError({ teamName: "Team name is required" });
            return;
        }

        if (!email.trim()) {
            setError({ email: "Email is required" });
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError({ email: "Please enter a valid email address" });
            return;
        }

        setIsPending(true);

        try {
           const payload = {
                name: email.trim(), // backend field name
                requested_team_name: teamName.trim(), // backend field name
                };

                await api.post(REQUEST_TEAM_API, payload);
            alert("Your request has been sent! We will get back to you soon.");
            navigate('/login');
        } catch (err: any) {
            const errorMsg = err?.response?.data?.message || err?.message || "Failed to send request. Please try again.";
            setError({ General: errorMsg });
        } finally {
            setIsPending(false);
        }
    };

    const goBack = () => {
        navigate('/signup');
    };

    return (
        <div className="w-full min-h-screen flex flex-col justify-center items-center box-border bg-gradient-to-br from-gray-100 to-cyan-50 py-12 px-4">
            <form
                onSubmit={handleSubmit}
                className="bg-white w-full max-w-[494px] p-10 rounded-[20px] shadow-lg"
            >
                {/* Back Arrow */}
                <div className="flex justify-start mb-4">
                    <button
                        type="button"
                        onClick={goBack}
                        className="flex items-center text-gray-600 hover:text-gray-800 cursor-pointer"
                    >
                        <img
                            loading="lazy"
                            srcSet="/arrow_left.png"
                            className="w-[24px] h-[24px]"
                            alt="Back"
                        />
                    </button>
                </div>

                {/* Header */}
                <div className="flex flex-col items-start mb-6">
                    <h1 className="text-xl font-medium text-[#292929]">Contact</h1>
                    <p className="mt-2 text-[13px] font-normal text-[#5B5B5B]">
                        Enter your team details and we will add your team to the platform.
                    </p>
                </div>

                {/* Team Name */}
                <div className="flex flex-col mb-4">
                    <label htmlFor="teamName" className="text-sm font-medium text-black mb-2">
                        Team Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        id="teamName"
                        type="text"
                        placeholder="Enter your team/college name"
                        value={teamName}
                        onChange={(e) => setTeamName(e.target.value)}
                        className={`bg-[#F5F5F5] focus:bg-[#F6FBFE] focus:border-[#2BACC9] focus:outline-none border-0 focus:border w-full py-2 pl-3 rounded ${
                            error.teamName ? 'border border-red-500' : ''
                        }`}
                    />
                    {error.teamName && (
                        <p className="mt-1 text-xs text-red-500">{error.teamName}</p>
                    )}
                </div>

                {/* Email */}
                <div className="flex flex-col mb-4">
                    <label htmlFor="email" className="text-sm font-medium text-black mb-2">
                        Email <span className="text-red-500">*</span>
                    </label>
                    <input
                        id="email"
                        type="email"
                        placeholder="Enter your email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={`bg-[#F5F5F5] focus:bg-[#F6FBFE] focus:border-[#2BACC9] focus:outline-none border-0 focus:border w-full py-2 pl-3 rounded ${
                            error.email ? 'border border-red-500' : ''
                        }`}
                    />
                    {error.email && (
                        <p className="mt-1 text-xs text-red-500">{error.email}</p>
                    )}
                </div>

                {/* General Error */}
                {error.General && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
                        <p className="text-sm text-red-600">{error.General}</p>
                    </div>
                )}

                {/* Submit Button */}
                <div className="flex flex-col mt-6 justify-center items-center">
                    <button
                        type="submit"
                        disabled={isPending}
                        className="flex flex-row justify-center items-center cursor-pointer self-stretch px-16 py-3 text-center text-lg text-white whitespace-nowrap bg-cyan-300 rounded hover:bg-cyan-400 disabled:opacity-50 max-md:px-5 max-md:max-w-full"
                    >
                        {isPending ? "Sending..." : "Submit"}
                    </button>
                </div>
            </form>
        </div>
    );
}
